import React, { ReactElement, useState, useEffect } from "react";
import {
  Box,
  Drawer,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputBase,
  TablePagination,
  TableSortLabel,
  CircularProgress,
} from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import DrawerToolbar from "common/components/drawer/drawer-toolbar";
import CustomerAPI, {
  OwnedUnitsRequest,
  GetOwnedUnitsResponse,
} from "components/customer/api/CustomerAPI";
import { OwnedUnitDto } from "common/models/customer";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawerPaper: {
      width: "70%",
      maxWidth: 1200,
      padding: theme.spacing(2),
    },
    drawerHeader: {
      marginTop: theme.spacing(-1),
      marginBottom: theme.spacing(1),
    },
    root: {
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "column",
    },
    tableContainer: {
      maxHeight: "70vh",
      boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
      border: "1px solid #EAEDED",
    },
    table: {
      "& th": {
        backgroundColor: "#EAECEE",
        fontWeight: 600,
        fontSize: "0.85rem",
        padding: "6px 8px",
        whiteSpace: "nowrap",
        position: "sticky",
        top: 0,
        zIndex: 2,
      },
      "& td": {
        borderBottom: "1px solid #EAEDED",
        fontSize: "0.85rem",
        padding: "6px 8px",
        wordBreak: "break-word",
      },
      "& tr:nth-of-type(odd)": {
        backgroundColor: "#F9FAFB",
      },
      "& tr:hover": {
        backgroundColor: "#F2F3F4",
      },
    },
    searchBox: {
      width: "50%",
      display: "flex",
      alignItems: "center",
      border: "1px solid #ccc",
      borderRadius: 4,
      padding: "2px 10px",
      marginTop: 12,
      marginBottom: 12,
      "&:focus-within": {
        borderColor: theme.palette.primary.main,
      },
    },
    searchInput: {
      marginLeft: theme.spacing(1),
      flex: 1,
      fontSize: 14,
    },
    paginationRow: {
      position: "sticky",
      bottom: 0,
      backgroundColor: "#FFFFFF !important",
      zIndex: 2,
      "& td": {
        borderTop: "1px solid #EAEDED",
        borderBottom: "none",
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
      },
    },
  })
);

const columns: { id: keyof OwnedUnitDto; label: string }[] = [
  { id: "make", label: "Make" },
  { id: "model", label: "Model #" },
  { id: "unit", label: "Unit #" },
  { id: "receivedDate", label: "Purchase Date" },
  { id: "serialNumber", label: "Serial No" },
  { id: "year", label: "Year" },
  { id: "price", label: "Price" },
];

type Order = "asc" | "desc";

export default function OwnedUnitsDrawer({
  open,
  onClose,
  customerId,
  onNoRecords,
}: {
  open: boolean;
  onClose: () => void;
  customerId: number | string;
  onNoRecords?: () => void;
}): ReactElement {
  const classes = useStyles();

  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof OwnedUnitDto>("make");
  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState<OwnedUnitDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);

 useEffect(() => {
  if (
    !open ||
    customerId == null ||
    Number(customerId) <= 0 
  ) {
    return;
  }

  const fetchUnits = async () => {
    try {
      setLoading(true);
      const request: OwnedUnitsRequest = {
        pageNumber: page + 1,
        pageSize: rowsPerPage,
        customerId: Number(customerId),
      };

      const response: GetOwnedUnitsResponse = await CustomerAPI.getOwnedUnits(request);
      const unitsData = response.units || [];
      setUnits(unitsData);
      setTotalCount(response.totalCount || 0);

      if (unitsData.length === 0 && response.totalCount === 0) {
        onNoRecords?.();
      }
    } catch (err) {
      console.error("Failed to fetch owned units:", err);
      setUnits([]);
      setTotalCount(0);
      onNoRecords?.();
    } finally {
      setLoading(false);
    }
  };

  fetchUnits();
}, [open, customerId, page, rowsPerPage]);




  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: keyof OwnedUnitDto) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredData = units.filter((row) => {
    if (!searchText) return true;
    const searchWords = searchText.toLowerCase().trim().split(/\s+/);
    const fieldsToSearch = [
      row.make,
      row.model,
      row.unit,
      row.receivedDate,
      row.serialNumber,
      row.year && row.year !== 0 ? row.year.toString() : "",
      row.price?.toString(),
      row.hourmeter,
      row.branch,
      row.manufacturerName,
      row.modelDescription,
      row.status,
    ];
    return searchWords.every((word) =>
      fieldsToSearch.some((field) => field?.toString().toLowerCase().includes(word))
    );
  });

  const sortedData = [...filteredData].sort((a, b) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];
    const numericColumns: (keyof OwnedUnitDto)[] = ["year", "price", "hourmeter"];
    const isNumeric = numericColumns.includes(orderBy);
    if (isNumeric) {
      const aNum = Number(aValue) || 0;
      const bNum = Number(bValue) || 0;
      return order === "asc" ? aNum - bNum : bNum - aNum;
    }
    const aStr = aValue?.toString() || "";
    const bStr = bValue?.toString() || "";
    if (aStr < bStr) return order === "asc" ? -1 : 1;
    if (aStr > bStr) return order === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      variant="temporary"
      classes={{ paper: classes.drawerPaper }}
      anchor="right"
      SlideProps={{ timeout: { enter: 400, exit: 500 } }}
    >
      <Box className={classes.drawerHeader}>
        <DrawerToolbar title="Owned Units" onClose={onClose} />
      </Box>

      <Container className={classes.root}>
        <Box className={classes.searchBox}>
          <SearchIcon fontSize="small" />
          <InputBase
            placeholder="Search..."
            value={searchText}
            onChange={handleSearchChange}
            className={classes.searchInput}
          />
        </Box>

        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table className={classes.table} size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.id}>
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : "asc"}
                      onClick={() => handleRequestSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ padding: "20px 0" }}>
                    <CircularProgress size={26} color="inherit" style={{ color: "#9e9e9e" }} />
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row, idx) => (
                  <TableRow hover key={idx}>
                    <TableCell>{row.make || "-"}</TableCell>
                    <TableCell>{row.model || "-"}</TableCell>
                    <TableCell>{row.unit || "-"}</TableCell>
                    <TableCell>
                      {row.receivedDate && row.receivedDate !== "1900-01-01T00:00:00"
                        ? (() => {
                          const d = new Date(row.receivedDate);
                          const day = String(d.getDate()).padStart(2, "0");
                          const month = String(d.getMonth() + 1).padStart(2, "0");
                          const year = d.getFullYear();
                          return `${day}/${month}/${year}`;
                        })()
                        : ""}
                    </TableCell>
                    <TableCell>{row.serialNumber || "-"}</TableCell>
                    <TableCell>{row.year && row.year !== 0 ? row.year : ""}</TableCell>
                    <TableCell>
                      {row.price
                        ? `$${row.price.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                        : ""}
                    </TableCell>
                  </TableRow>
                ))
              )}
              <TableRow className={classes.paginationRow}>
                <TableCell colSpan={8}>
                  <TablePagination
                    rowsPerPageOptions={[10, 15, 20]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Rows per page:"
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Drawer>
  );
}
